import { useEffect, useState } from "react";
import { FaceCameraPreview } from "@/components/auth/face-camera-preview";
import { getEnrollment } from "@/lib/face/face-enrollment-store";
import {
  getFaceWatchState,
  getLastFaceObservation,
  subscribeFaceObservations,
  subscribeFaceWatchState,
} from "@/lib/face/face-watcher";
import { useUserPreferences } from "@/hooks/use-user-preferences";

/**
 * Lock-screen face unlock indicator: the camera, boxed faces, and what it's currently making of
 * them.
 *
 * The lock screen is where face unlock is supposed to do its job, so it's also where its silence
 * is most confusing — someone stood waiting to be recognized has no way to tell "the camera is
 * off", "it can't see you", and "it sees you but doesn't know you" apart. Each needs a different
 * response from them, so each gets a different line.
 *
 * Renders nothing unless the watcher is actually running (face unlock off, or nobody enrolled),
 * so the lock screen is untouched for everyone not using this.
 */
export function FaceSignInStatus({ className }) {
  const [watch, setWatch] = useState(() => getFaceWatchState());
  const [faces, setFaces] = useState(() => getLastFaceObservation()?.faces ?? []);
  // Recognizing someone and being allowed to sign them in are different switches: with
  // "Sign me in when recognized" off, the camera still watches (for the stranger lock) but the
  // policy will never mint a session. Promising a sign-in that is turned off is a lie the user
  // has no way to see through.
  const canSignIn = useUserPreferences().faceAutoSwitchEnabled;

  useEffect(() => subscribeFaceWatchState(setWatch), []);
  useEffect(() => subscribeFaceObservations((observation) => setFaces(observation.faces)), []);

  if (watch.status === "off") return null;

  // Only faces close enough to be AT the monitor count — someone crossing the room in the
  // background shouldn't make this say "we don't recognize you".
  const present = faces.filter((face) => face.isAtMonitor);
  const known = present.find((face) => face.userId && getEnrollment(face.userId));
  // Matching the face is only half of it: the policy also demands the anti-spoof and liveness
  // checks pass before it will mint a session. Claiming "signing you in…" on the match alone
  // promises something that may never arrive, which is exactly how this feature earns a
  // reputation for being broken — so the two states are reported separately.
  // Temporarily ignore liveness for lock-screen messaging (must stay in sync with
  // face-auth-policy's lock-screen login gate).
  // const signingIn = known?.isRealPerson && canSignIn;
  const signingIn = !!known && canSignIn;

  let message = "Looking for a familiar face…";
  let tone = "muted";
  if (watch.status === "starting") {
    message = "Getting ready…";
  } else if (watch.status === "error") {
    // Actionable only from a signed-in machine, but saying so beats leaving someone waiting on
    // a camera that is never going to answer.
    message =
      watch.errorReason === "camera-denied"
        ? "Camera access is blocked — sign in with your PIN."
        : "Camera unavailable — sign in with your PIN.";
    tone = "warn";
  } else if (signingIn) {
    const name = getEnrollment(known.userId)?.displayName || "";
    message = name ? `Welcome back, ${name} — signing you in…` : "Signing you in…";
    tone = "good";
  } else if (known && !canSignIn) {
    // Recognized, but automatic sign-in is switched off — so nothing more is going to happen
    // and standing there waiting is pointless. Name the switch, since it is the fix.
    const name = getEnrollment(known.userId)?.displayName || "";
    message = name
      ? `Hi ${name} — automatic sign-in is off. Tap your profile.`
      : "Automatic sign-in is off. Tap your profile.";
  // } else if (known) {
  //   // Recognized, but not yet proven to be a live person rather than a photo of one. Usually
  //   // momentary; if it sticks, it is the honest reason sign-in hasn't happened.
  //   message = "Checking it's really you…";
  } else if (present.length) {
    message = "We don't recognize you — pick your profile below.";
  }

  return (
    <div className={className}>
      <div className="flex flex-col items-center gap-2.5">
        <FaceCameraPreview className="w-40" />
        <p
          className={
            tone === "good"
              ? "text-xs font-medium text-emerald-500"
              : tone === "warn"
                ? "text-xs text-amber-600 dark:text-amber-500"
                : "text-xs text-muted-foreground"
          }
          // polite, not assertive: this narrates an ambient camera, and a screen-reader user
          // tabbing through the roster must not have it interrupt them every 500ms.
          aria-live="polite"
        >
          {message}
        </p>
      </div>
    </div>
  );
}
